import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { ShortcutAction } from 'src/app/core/enums/shortcut-action.enum';
import { SIDEBAR_PAGE_MAPPING } from 'src/app/core/constants/sidebar-module-mapping';

const SHORTCUT_LIST_REDIRECT_BYPASS_QUERY = 'skipWizardListRedirect=1';

/** DOM event for contextual shortcuts (save / print / search). Screens may listen on `document`. */
export const ERP_SHORTCUT_INTENT_EVENT = 'erp-shortcut-intent';

export type ErpShortcutIntentType = 'save' | 'print' | 'search';

export interface ErpShortcutIntentDetail {
  action: ShortcutAction;
  type: ErpShortcutIntentType;
}

const NAVIGATION_BY_ACTION: Partial<Record<ShortcutAction, string>> = {
  [ShortcutAction.NewSalesInvoice]: SIDEBAR_PAGE_MAPPING.Sales.createPath,
  [ShortcutAction.NewPurchaseInvoice]: SIDEBAR_PAGE_MAPPING.Purches.createPath,
  [ShortcutAction.NewReceiptVoucher]: SIDEBAR_PAGE_MAPPING.ReceiptVoucher.createPath,
  [ShortcutAction.NewPaymentVoucher]: SIDEBAR_PAGE_MAPPING.PaymentVoucher.createPath,
  [ShortcutAction.NewJournal]: SIDEBAR_PAGE_MAPPING.Journal.createPath,
  [ShortcutAction.OpenPOS]: SIDEBAR_PAGE_MAPPING.POSSales.createPath,
  [ShortcutAction.NewCustomer]: SIDEBAR_PAGE_MAPPING.Customer.createPath,
  [ShortcutAction.NewVendor]: SIDEBAR_PAGE_MAPPING.Vendor.createPath,
  [ShortcutAction.NewProduct]: SIDEBAR_PAGE_MAPPING.Products.createPath,
};

const CONTEXT_INTENT_BY_ACTION: Partial<Record<ShortcutAction, ErpShortcutIntentType>> = {
  [ShortcutAction.Save]: 'save',
  [ShortcutAction.Print]: 'print',
  [ShortcutAction.Search]: 'search',
};

/**
 * Executes shortcut actions: navigates for screen-level shortcuts, or dispatches a DOM intent for contextual ones.
 */
@Injectable({
  providedIn: 'root',
})
export class ShortcutActionRouterService {
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly uiActionBusSubject = new Subject<ErpShortcutIntentDetail>();
  readonly uiActionBus$: Observable<ErpShortcutIntentDetail> = this.uiActionBusSubject.asObservable();

  execute(action: ShortcutAction): void {
    const createPath = NAVIGATION_BY_ACTION[action];
    if (createPath) {
      this.navigateToCreateScreen(createPath);
      return;
    }

    const intent = CONTEXT_INTENT_BY_ACTION[action];
    if (intent) {
      this.dispatchContextIntent(action, intent);
    }
  }

  private navigateToCreateScreen(path: string): void {
    const separator = path.includes('?') ? '&' : '?';
    const shortcutTarget = `${path}${separator}${SHORTCUT_LIST_REDIRECT_BYPASS_QUERY}`;
    setTimeout(() => {
      void this.router.navigateByUrl(shortcutTarget);
    }, 0);
  }

  private dispatchContextIntent(action: ShortcutAction, type: ErpShortcutIntentType): void {
    const detail: ErpShortcutIntentDetail = { action, type };
    this.uiActionBusSubject.next(detail);
    this.document.dispatchEvent(
      new CustomEvent<ErpShortcutIntentDetail>(ERP_SHORTCUT_INTENT_EVENT, {
        bubbles: true,
        cancelable: true,
        detail,
      })
    );
  }
}
